import { cloneDeep } from 'lodash';
import {
  ColumnAlign,
  ColumnWidths,
  DataRowAction,
  Field,
  FieldType,
  FilterOperator,
  IntegerField,
  Option,
  PersistenceType,
  Primitive,
  DecimalField,
} from './types';
import { displayLocaleDate, displayLocaleDateTime } from './DateTimeUtils';

export const cloneField = (field: Field): Field => cloneDeep(field);

export const getDisplayTextFromPrimitive = (field: Field, value: Primitive) =>
  getDisplayTextFromString(field, getFieldStringFromPrimitive(field.type, value));

export const getDisplayTextFromString = (field: Field, value: string): string => {
  if (field.getDisplayText) {
    return field.getDisplayText(value);
  }
  let display = value;
  if (field.type === FieldType.Boolean) {
    display = value === 'true' ? 'yes' : 'no';
  }
  if (field.type === FieldType.Date && !!value) {
    const d = new Date(value);
    display = displayLocaleDate(d);
  }
  if (field.type === FieldType.Datetime && !!value) {
    const d = new Date(value);
    display = displayLocaleDateTime(d);
  }
  if (field.type === FieldType.Options && value !== '') {
    const option = field.options.find((opt) => opt.id === String(value));
    display = option ? option.label : `[${value}: unknown]`;
  }
  if ([FieldType.Decimal, FieldType.Integer].indexOf(field.type) > -1) {
    display = parseFloat(value).toFixed((field as DecimalField).decimalDigits || 0);
    if (display === 'NaN') {
      display = '-';
    }
  }
  if (field.staticPrefix) {
    display = field.staticPrefix + display;
  }
  if (field.staticSuffix) {
    display += field.staticSuffix;
  }
  return display;
};

const COLUMN_ALIGNS: Partial<Record<FieldType, ColumnAlign>> = {
  [FieldType.Decimal]: 'right',
  [FieldType.Integer]: 'right',
};
export const getFieldColumnAlign = (field: Field): ColumnAlign =>
  field.listColumnAlign || COLUMN_ALIGNS[field.type] || 'left';

export const getFieldColumnStyle = (field: Field, row: any): any =>
  field.listCellStyle ? field.listCellStyle(row) : {};

const COLUMN_WIDTHS: Partial<Record<FieldType, ColumnWidths>> = {
  [FieldType.Richtext]: {
    xs: 12,
    sm: 12,
    md: 12,
    lg: 12,
    xl: 6,
    xxl: 6,
  },
};

export const getFieldColumnWidths = (field: Field): Partial<ColumnWidths> => {
  if (field.formColumnWidths) {
    return field.formColumnWidths;
  }
  return getFieldTypeColumnWidths(field.type);
};

export const getFieldTypeColumnWidths = (fieldType: FieldType): ColumnWidths => {
  if (COLUMN_WIDTHS[fieldType]) {
    return COLUMN_WIDTHS[fieldType];
  }
  return {
    xs: 12,
    sm: 12,
    md: 6,
    lg: 4,
    xl: 3,
    xxl: 3,
  };
};

export const getFieldPrimitiveFromString = (fieldType: FieldType, value: string): Primitive => {
  if (fieldType === FieldType.Boolean) {
    return value === 'true';
  }
  if ([FieldType.Decimal, FieldType.Integer].indexOf(fieldType) > -1) {
    const num = parseFloat(value);
    return Number.isFinite(num) ? num : null;
  }
  if (value === '') {
    return null;
  }
  return value;
};

export const getFieldStringFromPrimitive = (fieldType: FieldType, value: Primitive | object): string => {
  if (fieldType === FieldType.Boolean && typeof value === 'boolean') {
    return value ? 'true' : 'false';
  }
  if (value === undefined || value === null) {
    return '';
  }
  if (fieldType === FieldType.Object && typeof value === 'object') {
    return JSON.stringify(value);
  }
  return String(value);
};

const RANGE_FIELD_TYPES = [FieldType.Date, FieldType.Datetime, FieldType.Decimal, FieldType.Integer];

export const getFieldTypeDefaultFilter = (fieldType: FieldType): FilterOperator => {
  if (RANGE_FIELD_TYPES.indexOf(fieldType) > -1) {
    return 'eq'; // equals
  }
  if (fieldType === FieldType.Boolean || fieldType === FieldType.Options || fieldType === FieldType.Reference) {
    return 'eq'; // equals
  }
  return 'ct'; // contains
};

export const getFieldTypeFilterOptions = (fieldType: FieldType): Option[] => {
  if (RANGE_FIELD_TYPES.indexOf(fieldType) > -1) {
    return [
      { id: 'eq', label: 'equals' },
      { id: 'ne', label: 'not equal to' },
      { id: 'gt', label: 'greater than' },
      { id: 'lt', label: 'less than' },
      { id: 'bl', label: 'is blank' },
      { id: 'nb', label: 'is non-blank' },
      { id: 'in', label: 'is in list', active: false },
      { id: 'ni', label: 'is not in list', active: false },
    ];
  }
  if (fieldType === FieldType.Boolean) {
    return [
      { id: 'eq', label: 'equals' },
      { id: 'ne', label: 'not equal to' },
      { id: 'bl', label: 'is blank' },
      { id: 'nb', label: 'is non-blank' },
    ];
  }
  if (fieldType === FieldType.Options || fieldType === FieldType.Reference) {
    return [
      { id: 'eq', label: 'equals' },
      { id: 'ne', label: 'not equal to' },
      { id: 'bl', label: 'is blank' },
      { id: 'nb', label: 'is non-blank' },
      { id: 'in', label: 'is in list', active: false },
      { id: 'ni', label: 'is not in list', active: false },
    ];
  }
  return [
    { id: 'eq', label: 'equals' },
    { id: 'ne', label: 'not equal to' },
    { id: 'sw', label: 'starts with' },
    { id: 'ew', label: 'ends with' },
    { id: 'ct', label: 'contains' },
    { id: 'bl', label: 'is blank' },
    { id: 'nb', label: 'is non-blank' },
  ];
};

export const getTooltipHoverText = (field: Field, row: any): string =>
  field.tooltipText ? field.tooltipText(row) : '';

export const isFieldEditable = (field: Field, rowAction: DataRowAction): boolean => {
  if (typeof field.editable === 'boolean') {
    return field.editable;
  }
  if (field.persistence === PersistenceType.SetOnCreateOnly) {
    return rowAction === 'C';
  }
  if (
    [PersistenceType.BackEndManaged, PersistenceType.BackEndOnly, PersistenceType.BackEndGenerated].indexOf(
      field.persistence || PersistenceType.Normal,
    ) > -1
  ) {
    return false;
  }
  return true;
};

export const isFieldFilterable = (field: Field): boolean => {
  if (typeof field.filterable === 'boolean') {
    return field.filterable;
  }
  if (
    !!field.evaluateFrontEndIn ||
    !!field.evaluateBackEndIn ||
    !!field.evaluateBackEndOut /* || !!field.encrypted */
  ) {
    return false;
  }
  return [FieldType.File, FieldType.Flex].indexOf(field.type) === -1;
};

export const isFieldSortable = (field: Field): boolean => {
  if (typeof field.sortable === 'boolean') {
    return field.sortable;
  }
  if (!!field.evaluateFrontEndIn || !!field.evaluateBackEndIn || !!field.evaluateBackEndOut || !!field.encrypted) {
    return false;
  }
  if (field.type === FieldType.Reference) {
    return !!field.refEntity.refOrderClause;
  }
  return [FieldType.File, FieldType.Flex, FieldType.Richtext].indexOf(field.type) === -1;
};

export const makeClone = (seqIncrement?: number) => {
  let seq = 0;
  return (field: Field, overrides?: Partial<Field>): Field => {
    seq += seqIncrement || 5;
    const out = Object.assign({}, field, { seq }, overrides);
    delete (out as IntegerField).autoIncrement;
    if (overrides?.seq !== undefined) {
      seq = overrides.seq;
    }
    return out;
  };
};
